import emailjs from '@emailjs/browser';
import { EMAIL_CONFIG } from '../config/emailConfig';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

class EmailService {
  constructor() {
    console.log('🚀 Inicializando EmailService...');
    console.log('📧 Configuración EmailJS:', {
      SERVICE_ID: EMAIL_CONFIG.SERVICE_ID,
      TEMPLATE_ID: EMAIL_CONFIG.TEMPLATE_ID,
      PUBLIC_KEY: EMAIL_CONFIG.PUBLIC_KEY ? '✅ Configurada' : '❌ No configurada'
    });
    
    // Validar configuración
    if (!EMAIL_CONFIG.PUBLIC_KEY || EMAIL_CONFIG.PUBLIC_KEY === 'tu_public_key_aqui') {
      console.error('❌ PUBLIC_KEY no configurada correctamente');
      console.error('📋 Edita src/config/emailConfig.js con tus credenciales reales de EmailJS');
    }
    
    if (!EMAIL_CONFIG.SERVICE_ID || EMAIL_CONFIG.SERVICE_ID === 'service_belkysvet') {
      console.error('❌ SERVICE_ID no configurada correctamente');
    }
    
    if (!EMAIL_CONFIG.TEMPLATE_ID || EMAIL_CONFIG.TEMPLATE_ID === 'template_recordatorio') {
      console.error('❌ TEMPLATE_ID no configurada correctamente');
    }
    
    // Inicializar EmailJS con tu Public Key
    try {
      emailjs.init(EMAIL_CONFIG.PUBLIC_KEY);
      console.log('✅ EmailJS inicializado correctamente');
    } catch (error) {
      console.error('❌ Error inicializando EmailJS:', error);
    }
  }

  // Función para formatear fecha en español
  formatDateInSpanish(fecha) {
    console.log(`🔍 Fecha recibida para formatear: "${fecha}" (tipo: ${typeof fecha})`);
    
    // Asegurar que tenemos el formato correcto
    let fechaLimpia = fecha;
    if (typeof fecha === 'string') {
      // Si viene con hora, extraer solo la fecha
      fechaLimpia = fecha.split('T')[0];
      fechaLimpia = fechaLimpia.split(' ')[0];
    }
    
    console.log(`🔍 Fecha limpia: "${fechaLimpia}"`);
    
    // Usar dayjs para evitar problemas de zona horaria
    const date = dayjs(fechaLimpia);
    
    console.log(`🔍 Objeto dayjs creado:`, {
      isValid: date.isValid(),
      dateString: date.format('YYYY-MM-DD'),
      dayOfWeek: date.day(),
      dayOfMonth: date.date(),
      month: date.month(),
      year: date.year()
    });
    
    if (!date.isValid()) {
      console.error('❌ Fecha inválida:', fechaLimpia);
      return 'Fecha inválida';
    }
    
    const dias = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                   'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    
    const dayOfWeek = date.day();
    const dayOfMonth = date.date();
    const month = date.month();
    const year = date.year();
    
    const fechaFormateada = `${dias[dayOfWeek]} ${dayOfMonth} de ${meses[month]} de ${year}`;
    
    console.log(`📅 Resultado final: ${fechaLimpia} -> ${fechaFormateada}`);
    
    return fechaFormateada;
  }

  // Función para formatear tipo de cita
  formatAppointmentType(tipo) {
    const tipos = {
      'consulta': 'Consulta General',
      'vacunacion': 'Vacunación',
      'cirugia': 'Cirugía',
      'emergencia': 'Emergencia',
      'control': 'Control Post-Operatorio',
      'desparasitacion': 'Desparasitación',
      'revision': 'Revisión General'
    };
    return tipos[tipo] || tipo;
  }

  // Función principal para enviar recordatorio
  async sendAppointmentReminder(citaData) {
    console.log('📧 Iniciando envío de recordatorio...');
    console.log('📋 Datos de la cita:', citaData);
    
    try {
      // Validar configuración de EmailJS
      if (!EMAIL_CONFIG.PUBLIC_KEY || EMAIL_CONFIG.PUBLIC_KEY === 'tu_public_key_aqui') {
        throw new Error('❌ EmailJS no configurado: PUBLIC_KEY falta');
      }
      
      if (!EMAIL_CONFIG.SERVICE_ID || EMAIL_CONFIG.SERVICE_ID === 'service_belkysvet') {
        throw new Error('❌ EmailJS no configurado: SERVICE_ID falta');
      }
      
      if (!EMAIL_CONFIG.TEMPLATE_ID || EMAIL_CONFIG.TEMPLATE_ID === 'template_recordatorio') {
        throw new Error('❌ EmailJS no configurado: TEMPLATE_ID falta');
      }

      // Validar que tenemos todos los datos necesarios
      if (!citaData.email) {
        throw new Error('❌ Email del destinatario no proporcionado');
      }

      if (!citaData.paciente || !citaData.dueño || !citaData.fecha || !citaData.hora) {
        throw new Error('❌ Datos de la cita incompletos');
      }

      // Preparar los parámetros para el template
      console.log('📧 Datos de la cita recibidos:', {
        fecha: citaData.fecha,
        hora: citaData.hora,
        paciente: citaData.paciente,
        dueño: citaData.dueño,
        email: citaData.email,
        esRecordatorio: citaData.esRecordatorio || false
      });

      // Determinar el tipo de email
      const esRecordatorio = citaData.esRecordatorio === true;
      const tipoEmail = esRecordatorio ? 'RECORDATORIO' : 'CONFIRMACIÓN';
      const mensajeIntro = esRecordatorio 
        ? `Le recordamos que tiene una cita programada para ${citaData.paciente} en 1 hora:`
        : `Su cita para ${citaData.paciente} ha sido confirmada:`;

      const templateParams = {
        to_email: citaData.email,
        to_name: citaData.dueño,
        paciente_name: citaData.paciente,
        owner_name: citaData.dueño,
        appointment_date: this.formatDateInSpanish(citaData.fecha),
        appointment_time: citaData.hora,
        appointment_type: this.formatAppointmentType(citaData.tipo),
        motivo: citaData.motivo || '',
        observaciones: citaData.observaciones || '',
        telefono: citaData.telefono || '',
        // Parámetros específicos para recordatorios
        email_type: tipoEmail,
        intro_message: mensajeIntro,
        is_reminder: esRecordatorio ? 'true' : 'false',
        // Información de la clínica
        clinic_name: 'BelkysVeterinaria',
        clinic_phone: '+57 321 610 3829',
        clinic_email: 'belkysveterinaria@gmail.com'
      };

      console.log(`📤 Enviando ${tipoEmail} con parámetros:`, templateParams);
      console.log('🔧 Usando configuración:', {
        SERVICE_ID: EMAIL_CONFIG.SERVICE_ID,
        TEMPLATE_ID: EMAIL_CONFIG.TEMPLATE_ID
      });

      // Enviar el email usando EmailJS
      const response = await emailjs.send(
        EMAIL_CONFIG.SERVICE_ID,
        EMAIL_CONFIG.TEMPLATE_ID,
        templateParams
      );

      console.log('✅ Email enviado exitosamente:', response);
      return {
        success: true,
        message: 'Recordatorio enviado exitosamente',
        response: response
      };

    } catch (error) {
      console.error('Error enviando email:', error);
      return {
        success: false,
        message: error.message || 'Error desconocido al enviar email',
        error: error
      };
    }
  }

  // Función para enviar confirmación de cita
  async sendAppointmentConfirmation(citaData) {
    try {
      const templateParams = {
        ...citaData,
        to_email: citaData.email,
        to_name: citaData.dueño,
        paciente_name: citaData.paciente,
        owner_name: citaData.dueño,
        appointment_date: this.formatDateInSpanish(citaData.fecha),
        appointment_time: citaData.hora,
        appointment_type: this.formatAppointmentType(citaData.tipo),
        clinic_name: 'BelkysVeterinaria'
      };

      const response = await emailjs.send(
        EMAIL_CONFIG.SERVICE_ID,
        'template_confirmacion', // Template diferente para confirmación
        templateParams
      );

      return {
        success: true,
        message: 'Confirmación enviada exitosamente',
        response: response
      };

    } catch (error) {
      console.error('Error enviando confirmación:', error);
      return {
        success: false,
        message: error.message || 'Error al enviar confirmación',
        error: error
      };
    }
  }
}

// Crear instancia única del servicio
const emailService = new EmailService();

export default emailService;
